import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider, type RouteObject } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@fontsource/bricolage-grotesque/400.css';
import '@fontsource/bricolage-grotesque/500.css';
import '@fontsource/bricolage-grotesque/600.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import './styles/app.css';
import { registrarDicionario } from './i18n';
import { useSessao } from './nucleo/sessao';
import { useEu } from './nucleo/auth/sessao';
import { DefinirSenha, Login, Recuperar } from './nucleo/auth/Login';
import { Shell } from './nucleo/layout/Shell';
import { Inicio } from './nucleo/paginas/Inicio';
import { Secao } from './nucleo/paginas/Secao';
import { MODULOS_WEB } from './nucleo/modulos';
import { Carregando, Erro } from './nucleo/ui/Estados';
import { ProvedorToast } from './nucleo/ui/Sobreposicoes';
import { MfaConta } from './nucleo/mfa/MfaConta';
import { opcoesQueries } from './nucleo/repeticao-queries';

for (const m of MODULOS_WEB) registrarDicionario(m.slug, m.dicionario);

const qc = new QueryClient({ defaultOptions: { queries: opcoesQueries } });

/**
 * Gate de sessão: sem sessão, só as telas públicas existem (entrar e recuperar). Definir senha abre mesmo com
 * sessão, porque o link do convite ou da recuperação carrega o próprio token de uso único.
 */
function Raiz() {
  const eu = useEu();
  const caminho = window.location.pathname;
  if (caminho === '/definir-senha') return <DefinirSenha />;
  if (eu.isPending) return <Carregando />;
  if (eu.isError) return <Erro aoTentar={() => void eu.refetch()} />;
  if (!eu.data) return caminho === '/recuperar' ? <Recuperar /> : <Login />;
  return <Shell eu={eu.data} />;
}

function SecaoComSessao({ menu }: { menu: string }) {
  const eu = useSessao();
  return <Secao menu={menu} modulos={MODULOS_WEB} eu={eu} />;
}

// Uma seção por menu com as abas de todos os módulos daquele menu; rotas extras (fichas) ficam na raiz.
const menus = [...new Set(MODULOS_WEB.map((m) => m.menu))];
const filhos: RouteObject[] = [
  { index: true, element: <Inicio /> },
  { path: 'conta', element: <MfaConta /> },
  ...menus.map((menu) => ({
    path: menu,
    element: <SecaoComSessao menu={menu} />,
    children: MODULOS_WEB.filter((m) => m.menu === menu).flatMap((m) => m.abas.map((a) => ({ path: a.caminho, element: a.elemento }))),
  })),
  ...MODULOS_WEB.flatMap((m) => m.rotas ?? []),
  { path: '*', element: <Navigate to="/" replace /> },
];

// Erro inesperado de tela vira o estado de erro do sistema (nunca a página padrão do roteador).
function ErroDeRota() {
  return (
    <div className="p-6">
      <Erro aoTentar={() => window.location.reload()} />
    </div>
  );
}

const router = createBrowserRouter([{ path: '/', element: <Raiz />, errorElement: <ErroDeRota />, children: filhos.map((f) => ({ ...f, errorElement: <ErroDeRota /> })) }]);

createRoot(document.getElementById('raiz')!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <ProvedorToast>
        <RouterProvider router={router} />
      </ProvedorToast>
    </QueryClientProvider>
  </StrictMode>,
);
